import type { Account, Trade, PropPayout, PropExpense } from '../types';

export interface PropFirmStats {
  currentBalance: number;
  totalPnL: number;
  highWaterMark: number; // highest balance achieved (for trailing)
  trailingFloor: number; // the lowest balance can go before breach
  drawdownUsed: number; // $ amount of drawdown consumed
  drawdownRemaining: number; // $ left before breach
  drawdownPct: number; // 0–100, how much of max DD is consumed
  todayPnL: number;
  dailyLimitUsed: number; // $ of daily limit consumed (only when negative day)
  dailyLimitRemaining: number; // $ left in daily limit today
  dailyLimitPct: number; // 0–100
  profitPnL: number; // total P&L toward profit target
  profitPct: number; // 0–100+ progress toward profit target
  profitRemaining: number; // $ still needed to hit target
  profitTargetBalance: number; // absolute balance that equals "passed"
  daysTraded: number; // unique days with at least one trade
  daysSinceStart: number; // calendar days since prop_start_date
  daysRemaining: number; // max_days - daysSinceStart (negative = overdue)
  status: 'safe' | 'warning' | 'danger' | 'breached' | 'passed';
  consistencyThreshold: number;
  consistencyMaxDayPct: number;
  consistencyWarning: boolean;
  intradayPeakToday: number | null; // highest unrealized balance reached today (trailing_intraday only)
  mfeCoveredCount: number; // trades with mfe_dollars stored
  mfeTotalCount: number; // total account trades
}

export function calcPropFirmStats(
  account: Account,
  trades: Trade[],
  liveBalance?: number,
): PropFirmStats {
  const {
    initial_balance: start,
    prop_drawdown_type: ddType = 'trailing_eod',
    prop_max_drawdown: maxDD = 0,
    prop_daily_limit: dailyLimit = 0,
    prop_profit_target: profitTarget = 0,
    prop_min_days: minDays = 0,
    prop_max_days: maxDays = 0,
    prop_start_date: startDate,
  } = account;

  // Filter only this account's trades from challenge start onwards, sorted oldest → newest
  const accountTrades = trades
    .filter((t) => t.account_id === account.id && (!startDate || t.trade_date >= startDate))
    .sort((a, b) => a.trade_date.localeCompare(b.trade_date));

  // Group by date → daily P&L (needed for todayPnL regardless of trailing type)
  const dailyMap: Record<string, number> = {};
  for (const t of accountTrades) {
    dailyMap[t.trade_date] = (dailyMap[t.trade_date] ?? 0) + t.pnl;
  }
  const sortedDays = Object.keys(dailyMap).sort();

  let runningBalance = start;
  let highWaterMark = start;
  let daysTraded = 0;
  let finalizedDay: string | null = null;
  let breachedInLoop = false;
  // Set when the trade replay reaches the profit target before any breach.
  // Passing is a historical event — once realized P&L hits the target it stays
  // passed, even if a later broker liveBalance (e.g. account reset after passing)
  // drops below the trailing floor. Authoritative over the liveBalance breach check.
  let passedInLoop = false;

  const isChallengePhaseFn = () =>
    (account.prop_phase ?? (account.account_type === 'demo' ? 'challenge' : 'funded')) ===
    'challenge';

  if (ddType === 'trailing_intraday') {
    // Trade-by-trade replay: HWM advances to intraday peak (entry balance + MFE)
    // then settles at entry balance + realized PnL.
    // When mfe_dollars is absent, fall back to max(0, pnl) — conservative but sound.
    const seenDays = new Set<string>();
    for (const trade of accountTrades) {
      if (finalizedDay) break;

      const tradeMfe = trade.mfe_dollars != null ? trade.mfe_dollars : Math.max(0, trade.pnl);
      const peakBalance = runningBalance + tradeMfe;
      if (peakBalance > highWaterMark) highWaterMark = peakBalance;

      runningBalance += trade.pnl;

      if (!seenDays.has(trade.trade_date)) {
        seenDays.add(trade.trade_date);
        daysTraded++;
      }

      const floor = highWaterMark - maxDD;
      if (maxDD > 0 && runningBalance <= floor) {
        finalizedDay = trade.trade_date;
        breachedInLoop = true;
        break;
      }

      const pnlSoFar = runningBalance - start;
      const minDaysMetSoFar = !minDays || daysTraded >= minDays;
      if (isChallengePhaseFn() && profitTarget > 0 && pnlSoFar >= profitTarget && minDaysMetSoFar) {
        finalizedDay = trade.trade_date;
        passedInLoop = true;
        break;
      }
    }
  } else {
    // EOD / static: day-by-day replay (original logic)
    for (const day of sortedDays) {
      runningBalance += dailyMap[day];
      if (ddType !== 'static' && runningBalance > highWaterMark) highWaterMark = runningBalance;
      daysTraded++;

      const floor = ddType === 'static' ? start - maxDD : highWaterMark - maxDD;

      if (maxDD > 0 && runningBalance <= floor) {
        finalizedDay = day;
        breachedInLoop = true;
        break;
      }

      const pnlSoFar = runningBalance - start;
      const minDaysMetSoFar = !minDays || daysTraded >= minDays;
      if (isChallengePhaseFn() && profitTarget > 0 && pnlSoFar >= profitTarget && minDaysMetSoFar) {
        finalizedDay = day;
        passedInLoop = true;
        break;
      }
    }
  }

  const currentBalance = liveBalance != null ? liveBalance : runningBalance;
  const totalPnL = currentBalance - start;

  // Consistency rule: funded = payout rule, challenge = passing rule. Threshold 0 = disabled.
  const consistencyThreshold = isChallengePhaseFn()
    ? (account.prop_challenge_consistency_pct ?? 0)
    : (account.prop_consistency_pct ?? 0);
  const tradePnL = runningBalance - start;
  let consistencyMaxDayPct = 0;
  if (consistencyThreshold > 0 && tradePnL > 0) {
    const maxDayPnl = Math.max(0, ...sortedDays.map((d) => dailyMap[d]).filter((v) => v > 0));
    consistencyMaxDayPct = (maxDayPnl / tradePnL) * 100;
  }
  const consistencyWarning =
    consistencyThreshold > 0 && consistencyMaxDayPct > consistencyThreshold;

  const trailingFloor = ddType === 'static' ? start - maxDD : highWaterMark - maxDD;

  const drawdownRemaining = currentBalance - trailingFloor;
  const drawdownUsed = Math.max(0, maxDD - drawdownRemaining);
  const drawdownPct = maxDD > 0 ? Math.min(100, (drawdownUsed / maxDD) * 100) : 0;

  // Today's P&L — zero if the account was finalized before today
  const today = new Date().toISOString().slice(0, 10);
  const todayPnL = finalizedDay && finalizedDay < today ? 0 : (dailyMap[today] ?? 0);

  // Daily limit: only counts when today is a losing day
  const dailyLossToday = todayPnL < 0 ? Math.abs(todayPnL) : 0;
  const dailyLimitUsed = dailyLimit > 0 ? Math.min(dailyLossToday, dailyLimit) : 0;
  const dailyLimitRemaining = dailyLimit > 0 ? Math.max(0, dailyLimit - dailyLossToday) : 0;
  const dailyLimitPct = dailyLimit > 0 ? Math.min(100, (dailyLossToday / dailyLimit) * 100) : 0;

  // Profit progress
  const profitPnL = totalPnL;
  const profitPct = profitTarget > 0 ? Math.min(100, (profitPnL / profitTarget) * 100) : 0;
  const profitRemaining = profitTarget > 0 ? Math.max(0, profitTarget - profitPnL) : 0;
  const profitTargetBalance = start + profitTarget;

  // Days
  let daysSinceStart = 0;
  let daysRemaining = 0;
  if (startDate) {
    const start_ms = new Date(startDate).getTime();
    const now_ms = new Date().getTime();
    daysSinceStart = Math.floor((now_ms - start_ms) / 86_400_000);
    daysRemaining = maxDays > 0 ? maxDays - daysSinceStart : 0;
  }

  // Status
  const minDaysMet = !minDays || daysTraded >= minDays;
  let status: PropFirmStats['status'] = 'safe';
  const passedInHistory =
    passedInLoop ||
    ((account.prop_phase ?? (account.account_type === 'demo' ? 'challenge' : 'funded')) ===
      'challenge' &&
      profitTarget > 0 &&
      profitPnL >= profitTarget &&
      minDaysMet);
  // breachedInLoop: drawdown breach detected during replay — authoritative even if liveBalance overrides currentBalance.
  if (breachedInLoop) {
    status = 'breached';
  } else if (passedInHistory) {
    // Passing is historical: once realized P&L hit the target before any breach,
    // a later liveBalance dip (e.g. account reset after passing) must not relabel it.
    status = 'passed';
  } else if (currentBalance <= trailingFloor) {
    status = 'breached';
  } else if (drawdownPct >= 80 || dailyLimitPct >= 80) {
    status = 'danger';
  } else if (drawdownPct >= 50 || dailyLimitPct >= 50) {
    status = 'warning';
  }
  // Overdue: past max days deadline but neither breached nor passed
  if (maxDays > 0 && daysRemaining < 0 && (status === 'safe' || status === 'warning')) {
    status = 'danger';
  }

  // Intraday peak for today + MFE coverage (trailing_intraday only)
  let intradayPeakToday: number | null = null;
  const mfeCoveredCount = accountTrades.filter((t) => t.mfe_dollars != null).length;
  const mfeTotalCount = accountTrades.length;

  if (ddType === 'trailing_intraday') {
    const todayTrades = accountTrades.filter((t) => t.trade_date === today);
    if (todayTrades.length > 0) {
      let balBeforeToday = start;
      for (const d of sortedDays) {
        if (d >= today) break;
        balBeforeToday += dailyMap[d];
      }
      let peak = balBeforeToday;
      let run = balBeforeToday;
      for (const t of todayTrades) {
        const mfe = t.mfe_dollars != null ? t.mfe_dollars : Math.max(0, t.pnl);
        peak = Math.max(peak, run + mfe);
        run += t.pnl;
      }
      intradayPeakToday = peak;
    }
  }

  return {
    currentBalance,
    totalPnL,
    highWaterMark,
    trailingFloor,
    drawdownUsed,
    drawdownRemaining,
    drawdownPct,
    todayPnL,
    dailyLimitUsed,
    dailyLimitRemaining,
    dailyLimitPct,
    profitPnL,
    profitPct,
    profitRemaining,
    profitTargetBalance,
    daysTraded,
    daysSinceStart,
    daysRemaining,
    status,
    consistencyThreshold,
    consistencyMaxDayPct,
    consistencyWarning,
    intradayPeakToday,
    mfeCoveredCount,
    mfeTotalCount,
  };
}

export interface DayResult {
  date: string; // 'YYYY-MM-DD'
  pnl: number;
  result: 'win' | 'loss' | 'flat' | 'off';
  isToday: boolean;
}

export function calcDayStreak(
  account: Account,
  trades: Trade[],
  days = 14,
  beRange: number = 0,
): DayResult[] {
  const accountTrades = trades.filter((t) => t.account_id === account.id);

  const byDate: Record<string, number> = {};
  for (const t of accountTrades) {
    byDate[t.trade_date] = (byDate[t.trade_date] ?? 0) + t.pnl;
  }

  const results: DayResult[] = [];
  const todayLocal = new Date();
  todayLocal.setHours(0, 0, 0, 0);

  const pad = (n: number) => String(n).padStart(2, '0');
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(todayLocal);
    d.setDate(d.getDate() - i);
    // Use LOCAL components — trade.trade_date is stored as local YYYY-MM-DD.
    // toISOString() would shift to UTC and mismatch by one day for users east of UTC.
    const dateStr = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const dow = d.getDay(); // 0=Sun 6=Sat
    const isToday = i === 0;

    if (dow === 0 || dow === 6) {
      results.push({ date: dateStr, pnl: 0, result: 'off', isToday });
      continue;
    }

    const pnl = byDate[dateStr];
    if (pnl === undefined) {
      results.push({ date: dateStr, pnl: 0, result: 'flat', isToday });
    } else if (beRange > 0 && Math.abs(pnl) <= beRange) {
      results.push({ date: dateStr, pnl, result: 'flat', isToday });
    } else if (pnl > 0) {
      results.push({ date: dateStr, pnl, result: 'win', isToday });
    } else if (pnl < 0) {
      results.push({ date: dateStr, pnl, result: 'loss', isToday });
    } else {
      results.push({ date: dateStr, pnl: 0, result: 'flat', isToday });
    }
  }

  return results;
}

export interface PortfolioStats {
  totalCapital: number; // sum of initial_balance for active prop accounts
  combinedPnL: number; // sum of totalPnL for active prop accounts
  netProfit: number; // totalPayouts - totalExpenses
  atRiskCount: number; // accounts with status 'warning' | 'danger'
  nextPayoutDays: number | null; // null = no funded accounts with target set
}

export function calcPortfolioStats(
  accounts: Account[],
  trades: Trade[],
  payouts: PropPayout[],
  expenses: PropExpense[],
): PortfolioStats {
  const propAccounts = accounts.filter(
    (a) => a.account_type === 'demo' || a.account_type === 'prop_firm',
  );

  const withStats = propAccounts.map((a) => ({
    account: a,
    stats: calcPropFirmStats(a, trades),
  }));

  const active = withStats.filter(
    (s) => s.stats.status !== 'breached' && s.stats.status !== 'passed',
  );

  const totalCapital = active.reduce((sum, s) => sum + s.account.initial_balance, 0);
  const combinedPnL = active.reduce((sum, s) => sum + s.stats.totalPnL, 0);

  const totalPayouts = payouts.reduce((sum, p) => sum + p.amount, 0);
  const totalExpenses = expenses.reduce((sum, e) => sum + e.amount, 0);
  const netProfit = totalPayouts - totalExpenses;

  const atRiskCount = active.filter(
    (s) => s.stats.status === 'warning' || s.stats.status === 'danger',
  ).length;

  const funded = active.filter(
    (s) =>
      s.account.account_type === 'prop_firm' &&
      (s.account.prop_profit_target ?? 0) > 0 &&
      s.stats.profitRemaining > 0,
  );

  let nextPayoutDays: number | null = null;

  for (const { account, stats } of funded) {
    const acctTrades = trades.filter((t) => t.account_id === account.id);
    const byDate: Record<string, number> = {};
    for (const t of acctTrades) {
      byDate[t.trade_date] = (byDate[t.trade_date] ?? 0) + t.pnl;
    }
    const winDays = Object.values(byDate).filter((p) => p > 0);
    if (winDays.length === 0) continue;

    const avgWinDay = winDays.reduce((a, b) => a + b, 0) / winDays.length;
    const days = Math.ceil(stats.profitRemaining / avgWinDay);

    if (nextPayoutDays === null || days < nextPayoutDays) nextPayoutDays = days;
  }

  return { totalCapital, combinedPnL, netProfit, atRiskCount, nextPayoutDays };
}
